/**
 * Order Service
 * Handles API calls for Made.bb orders
 */

import type { RollShadeOrder, MadebbSettings } from '../types/madebb.types';
import { authFetch } from '../utils/authFetch';

const API_BASE = '/api/madebb';

export class OrderService {
  /**
   * Get settings (includes order counter)
   */
  static async getSettings(): Promise<MadebbSettings> {
    const response = await authFetch(`${API_BASE}/settings`);
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch settings');
    }
    return data.settings;
  }

  /**
   * Create a new order
   */
  static async createOrder(orderData: Partial<RollShadeOrder>): Promise<RollShadeOrder> {
    // Order submission requires auth — the global requireAuth middleware applies to all POSTs.
    // Only staff (logged-in users) submit orders at this time. Use authFetch to send Bearer token.
    const response = await authFetch(`${API_BASE}/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(orderData)
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to create order');
    }
    return data.order;
  }

  /**
   * Get all orders
   */
  static async getOrders(): Promise<RollShadeOrder[]> {
    const response = await authFetch(`${API_BASE}/orders`);
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch orders');
    }
    return data.orders;
  }

  /**
   * Get a specific order (with optional security token)
   */
  static async getOrder(orderId: string, securityToken?: string): Promise<RollShadeOrder> {
    const url = securityToken
      ? `${API_BASE}/orders/${orderId}?token=${securityToken}`
      : `${API_BASE}/orders/${orderId}`;

    const response = await fetch(url);
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch order');
    }
    return data.order;
  }

  /**
   * Update an order
   */
  static async updateOrder(orderId: string, updates: Partial<RollShadeOrder>): Promise<RollShadeOrder> {
    const response = await authFetch(`${API_BASE}/orders/${orderId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });

    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to update order');
    }
    return data.order;
  }
}
